/**
 * PhoneVerificationDialog (ELARA-1169) — the web phone-verification flow in a modal.
 *
 * The web counterpart of mobile's `PhoneVerificationSheet`, mounted once at the app root so any screen
 * can present it with `phoneLinkState.openSheet()` (the Profile page's phone row does). All flow
 * state lives in the shared `PhoneLinkState` store, so this view is pure presentation over `status`:
 *
 *   manage (change) ⇄ editor (enter number) → code step (enter OTP) → verified → close.
 *
 * A verified number opens the **manage** view; adding one opens the editor. There is no "remove":
 * every member needs a number. The app-verification mechanism (invisible reCAPTCHA) lives behind the
 * injected `PhoneVerificationPort` (ELARA-730). Both verified views offer "Add Elara to Contacts", so
 * texts from the Elara number show who sent them (ELARA-1178). A member who must add a number before
 * using the app is sent to `VerifyPhonePage` instead (ELARA-1170).
 */

import { useState } from 'react';

import { usePhoneLinkState } from '#states/hooks.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FloatingInput,
  MaterialIcon,
} from '#ui/index.js';
import { PhoneInput } from '#ui/PhoneInput.js';

export function PhoneVerificationDialog() {
  const phoneLink = usePhoneLinkState();
  const [code, setCode] = useState('');

  const status = phoneLink.status;
  const inCodeStep = status === 'awaitingCode' || status === 'verifying';
  const justVerified = status === 'verified';
  const managing = status === 'idle' && phoneLink.isVerified;

  const close = () => {
    setCode('');
    phoneLink.closeSheet();
  };

  const handleConfirm = async () => {
    await phoneLink.confirmCode(code);
    if (phoneLink.status === 'verified') setCode('');
  };

  const handleChangeNumber = () => {
    setCode('');
    phoneLink.beginEdit();
  };

  return (
    <Dialog open={phoneLink.isSheetOpen} onOpenChange={(open) => !open && !phoneLink.isBusy && close()}>
      <DialogContent className="sm:max-w-md">
        {justVerified || managing ? (
          <>
            <DialogHeader className="items-center text-center space-y-2 pt-2">
              <MaterialIcon icon="verified" size="xl" filled className="text-accent" />
              <DialogTitle className="pr-0">Phone number verified</DialogTitle>
              <DialogDescription className="text-base text-foreground">{phoneLink.verifiedPhone}</DialogDescription>
              <p className="text-sm text-muted-foreground">
                You can message Elara from this number anytime with questions about your trips.
              </p>
            </DialogHeader>
            {justVerified ? (
              <DialogFooter className="flex-col gap-2 sm:flex-col">
                <Button variant="outline" className="w-full" onClick={() => void phoneLink.addElaraToContacts()}>
                  Add Elara to Contacts
                </Button>
                <Button className="w-full" onClick={close}>
                  Done
                </Button>
              </DialogFooter>
            ) : (
              <div className="flex flex-col gap-2">
                <Button className="w-full" onClick={handleChangeNumber}>
                  Change number
                </Button>
                <Button variant="outline" className="w-full" onClick={() => void phoneLink.addElaraToContacts()}>
                  Add Elara to Contacts
                </Button>
              </div>
            )}
          </>
        ) : inCodeStep ? (
          <>
            <DialogHeader>
              <DialogTitle>Enter your code</DialogTitle>
              <DialogDescription>
                Enter the 6-digit code we sent to {phoneLink.pendingPhone || 'your phone'}.
              </DialogDescription>
            </DialogHeader>
            <FloatingInput
              id="phoneVerificationCode"
              label="6-digit code"
              hideLabel
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
            />
            <Button className="w-full" onClick={() => void handleConfirm()} isBusy={status === 'verifying'}>
              Confirm
            </Button>
            <div className="flex justify-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void phoneLink.sendCode()}
                disabled={status === 'verifying'}
              >
                Resend code
              </Button>
              <Button variant="ghost" size="sm" onClick={handleChangeNumber} disabled={status === 'verifying'}>
                Change number
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Verify your phone number</DialogTitle>
              <DialogDescription>We’ll text you a code to confirm your number.</DialogDescription>
            </DialogHeader>
            <PhoneInput
              id="phoneNumber"
              hideLabel
              value={phoneLink.pendingPhone}
              onChange={(value) => phoneLink.setPendingPhone(value)}
            />
            <Button className="w-full" onClick={() => void phoneLink.sendCode()} isBusy={status === 'sendingCode'}>
              Send code
            </Button>
          </>
        )}

        {phoneLink.error && <p className="text-sm text-destructive">{phoneLink.error}</p>}
      </DialogContent>
    </Dialog>
  );
}
